import React, { useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';

// Activity type configuration matching the backend enum
const ACTIVITY_TYPES = [
  {
    category: 'Booking Lifecycle',
    types: [
      {
        type: 'booking_created',
        icon: '📝',
        label: 'Booking Created',
        description: 'Initial booking creation',
      },
      {
        type: 'booking_confirmed',
        icon: '✅',
        label: 'Booking Confirmed',
        description: 'Booking confirmed after payment',
      },
      {
        type: 'trip_started',
        icon: '🚗',
        label: 'Trip Started',
        description: 'Guest started the trip',
      },
      {
        type: 'trip_completed',
        icon: '🏁',
        label: 'Trip Completed',
        description: 'Trip successfully completed',
      },
    ],
  },
  {
    category: 'Trip Modifications',
    types: [
      {
        type: 'trip_extended',
        icon: '📅',
        label: 'Trip Extended',
        description: 'Guest extended the trip end date',
      },
      {
        type: 'trip_shortened',
        icon: '📅',
        label: 'Trip Shortened',
        description: 'Guest shortened the trip end date',
      },
    ],
  },
  {
    category: 'Car Swap',
    types: [
      {
        type: 'car_swap_requested',
        icon: '🔄',
        label: 'Car Swap Requested',
        description: 'Host requested a car swap',
      },
      {
        type: 'car_swap_approved',
        icon: '✅',
        label: 'Car Swap Approved',
        description: 'Guest approved the car swap',
      },
      {
        type: 'car_swap_rejected',
        icon: '❌',
        label: 'Car Swap Rejected',
        description: 'Guest rejected the car swap',
      },
      {
        type: 'car_swap_completed',
        icon: '🔄',
        label: 'Car Swap Completed',
        description: 'Car swap successfully completed',
      },
      {
        type: 'car_swap_expired',
        icon: '⏰',
        label: 'Car Swap Expired',
        description: 'Car swap request expired',
      },
    ],
  },
  {
    category: 'Cancellation',
    types: [
      {
        type: 'cancelled_by_guest',
        icon: '🚫',
        label: 'Cancelled by Guest',
        description: 'Booking cancelled by guest',
      },
      {
        type: 'cancelled_by_host',
        icon: '🚫',
        label: 'Cancelled by Host',
        description: 'Booking cancelled by host',
      },
      {
        type: 'cancelled_by_admin',
        icon: '🚫',
        label: 'Cancelled by Admin',
        description: 'Booking cancelled by admin',
      },
      {
        type: 'cancellation_penalty_waived',
        icon: '💰',
        label: 'Penalty Waived',
        description: 'Host waived cancellation penalty',
      },
    ],
  },
  {
    category: 'Security Deposit',
    types: [
      {
        type: 'deposit_collected',
        icon: '💳',
        label: 'Deposit Collected',
        description: 'Security deposit collected',
      },
      {
        type: 'deposit_authorized',
        icon: '💳',
        label: 'Deposit Authorized',
        description: 'Deposit authorization hold placed',
      },
      {
        type: 'deposit_charged',
        icon: '💳',
        label: 'Deposit Charged',
        description: 'Deposit charged (debit card)',
      },
      {
        type: 'deposit_hold_requested',
        icon: '⏸️',
        label: 'Deposit Hold Requested',
        description: 'Host requested to hold deposit',
      },
      {
        type: 'deposit_released',
        icon: '💰',
        label: 'Deposit Released',
        description: 'Full deposit released to guest',
      },
      {
        type: 'deposit_partially_released',
        icon: '💰',
        label: 'Deposit Partially Released',
        description: 'Partial deposit released',
      },
      {
        type: 'deposit_captured',
        icon: '💳',
        label: 'Deposit Captured',
        description: 'Authorization captured (damage claim)',
      },
    ],
  },
  {
    category: 'Payment',
    types: [
      {
        type: 'payment_received',
        icon: '💵',
        label: 'Payment Received',
        description: 'Payment successfully received',
      },
      {
        type: 'payment_failed',
        icon: '❌',
        label: 'Payment Failed',
        description: 'Payment attempt failed',
      },
    ],
  },
  {
    category: 'Refund',
    types: [
      {
        type: 'refund_initiated',
        icon: '🔄',
        label: 'Refund Initiated',
        description: 'Refund process started',
      },
      {
        type: 'refund_completed',
        icon: '💰',
        label: 'Refund Completed',
        description: 'Refund successfully completed',
      },
      {
        type: 'refund_failed',
        icon: '❌',
        label: 'Refund Failed',
        description: 'Refund attempt failed',
      },
    ],
  },
  {
    category: 'Driver',
    types: [
      {
        type: 'driver_added',
        icon: '👤',
        label: 'Driver Added',
        description: 'Additional driver added to booking',
      },
      {
        type: 'driver_removed',
        icon: '👤',
        label: 'Driver Removed',
        description: 'Driver removed from booking',
      },
    ],
  },
  {
    category: 'Other',
    types: [
      {
        type: 'protection_plan_changed',
        icon: '🛡️',
        label: 'Protection Plan Changed',
        description: 'Protection plan upgraded/changed',
      },
      {
        type: 'coupon_applied',
        icon: '🎫',
        label: 'Coupon Applied',
        description: 'Coupon code applied to booking',
      },
      {
        type: 'trip_start_photo_uploaded',
        icon: '📷',
        label: 'Start Photos Uploaded',
        description: 'Trip start photos uploaded',
      },
      {
        type: 'trip_end_photo_uploaded',
        icon: '📷',
        label: 'End Photos Uploaded',
        description: 'Trip end photos uploaded',
      },
    ],
  },
];

const TEST_FLOWS = [
  {
    id: 1,
    title: 'Booking Creation & Confirmation',
    steps: [
      'Log in as a guest',
      'Browse cars and select one',
      'Complete the checkout process',
      'Pay for the booking',
    ],
    expectedActivities: [
      'booking_created',
      'deposit_collected/deposit_authorized',
      'payment_received',
      'booking_confirmed',
    ],
    verification: 'Go to Booking Details → Scroll to "Activity Timeline"',
  },
  {
    id: 2,
    title: 'Trip Extension',
    steps: [
      'Log in as the guest who made the booking',
      'Go to booking details',
      'Click "Modify Booking"',
      'Select a later end date',
      'Complete payment for the extension',
    ],
    expectedActivities: ['trip_extended', 'payment_received'],
    verification:
      'Check Activity Timeline shows the extension with state change (old endAt → new endAt)',
  },
  {
    id: 3,
    title: 'Trip Shortening',
    steps: [
      'Log in as the guest',
      'Go to booking details',
      'Click "Modify Booking"',
      'Select an earlier end date',
      'Confirm the modification',
    ],
    expectedActivities: ['trip_shortened', 'refund_initiated', 'refund_completed'],
    verification:
      'Check Activity Timeline shows the shortening with state change and refund amount',
  },
  {
    id: 4,
    title: 'Guest Cancellation',
    steps: [
      'Log in as the guest',
      'Go to booking details',
      'Click "Cancel Booking"',
      'Confirm the cancellation',
    ],
    expectedActivities: [
      'cancelled_by_guest',
      'refund_initiated',
      'deposit_released',
      'refund_completed',
    ],
    verification: 'Check Activity Timeline shows cancellation chain with amounts',
  },
  {
    id: 5,
    title: 'Host Cancellation',
    steps: [
      'Log in as the host',
      'Go to Host Dashboard → Bookings',
      'Select a booking',
      'Click "Cancel Booking" (host action)',
      'Confirm the cancellation',
    ],
    expectedActivities: [
      'cancelled_by_host',
      'refund_initiated',
      'deposit_released',
      'refund_completed',
    ],
    verification: 'Check Activity Timeline shows host cancellation with full refund',
  },
  {
    id: 6,
    title: 'Host Waives Cancellation Penalty',
    steps: [
      'Log in as the host',
      'Go to booking details',
      'Click "Cancel Trip Without Penalty"',
      'Confirm the action',
    ],
    expectedActivities: ['cancellation_penalty_waived'],
    verification: 'Guest can now cancel without penalty',
  },
  {
    id: 7,
    title: 'Security Deposit Hold & Release',
    steps: [
      'Wait for trip to complete',
      'Log in as the host',
      'Go to booking details (Security Deposit section)',
      'Click "Release Deposit" and enter amount',
    ],
    expectedActivities: ['deposit_released OR deposit_partially_released'],
    verification: 'Check Activity Timeline shows deposit release with amount',
  },
  {
    id: 8,
    title: 'Car Swap Flow (Accept)',
    steps: [
      'Log in as the host',
      'Go to booking details',
      'Initiate car swap request',
      'Log in as the guest',
      'Accept the swap',
    ],
    expectedActivities: ['car_swap_requested', 'car_swap_approved', 'car_swap_completed'],
    verification: 'Check Activity Timeline shows the swap flow',
  },
  {
    id: 9,
    title: 'Car Swap Flow (Reject)',
    steps: [
      'Log in as the host',
      'Go to booking details',
      'Initiate car swap request',
      'Log in as the guest',
      'Reject the swap',
    ],
    expectedActivities: ['car_swap_requested', 'car_swap_rejected'],
    verification: 'Check Activity Timeline shows the rejection',
  },
  {
    id: 10,
    title: 'Protection Plan Upgrade',
    steps: [
      'Book a car with "Declined" protection plan',
      'Go to booking details',
      'Click "Upgrade" on a protection plan option',
    ],
    expectedActivities: ['protection_plan_changed'],
    verification: 'Check Activity Timeline shows the plan change',
  },
];

// Styled Components
const Container = styled.div`
  ${tw`max-w-6xl mx-auto p-6`}
`;

const Header = styled.div`
  ${tw`mb-8`}
`;

const Title = styled.h1`
  ${tw`text-3xl font-bold text-gray-900 mb-2`}
`;

const Subtitle = styled.p`
  ${tw`text-gray-600`}
`;

const TabContainer = styled.div`
  ${tw`flex gap-2 mb-6 border-b border-gray-200`}
`;

const Tab = styled.button<{ active: boolean }>`
  ${tw`px-4 py-2 font-medium transition-colors`}
  ${props =>
    props.active
      ? tw`text-blue-600 border-b-2 border-blue-600`
      : tw`text-gray-500 hover:text-gray-700`}
`;

const Section = styled.div`
  ${tw`mb-8`}
`;

const SectionTitle = styled.h2`
  ${tw`text-xl font-semibold text-gray-800 mb-4`}
`;

const CategoryCard = styled.div`
  ${tw`bg-white rounded-lg shadow-sm border border-gray-200 mb-4 overflow-hidden`}
`;

const CategoryHeader = styled.div`
  ${tw`bg-gray-50 px-4 py-3 border-b border-gray-200`}
`;

const CategoryTitle = styled.h3`
  ${tw`font-semibold text-gray-700`}
`;

const ActivityTable = styled.table`
  ${tw`w-full`}
`;

const TableRow = styled.tr`
  ${tw`border-b border-gray-100 last:border-0`}
`;

const TableCell = styled.td`
  ${tw`px-4 py-3 max-w-full min-w-0 box-border`}
`;

const IconCell = styled(TableCell)`
  ${tw`w-12 text-center text-xl`}
`;

const TypeCell = styled(TableCell)`
  ${tw`font-mono text-sm text-gray-600`}
`;

const LabelCell = styled(TableCell)`
  ${tw`font-medium text-gray-800`}
`;

const DescCell = styled(TableCell)`
  ${tw`text-gray-600 text-sm`}
`;

const TestFlowCard = styled.div`
  ${tw`bg-white rounded-lg shadow-sm border border-gray-200 p-5 mb-4`}
`;

const TestFlowHeader = styled.div`
  ${tw`flex items-center gap-3 mb-4`}
`;

const TestNumber = styled.div`
  ${tw`w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold text-sm`}
`;

const TestTitle = styled.h3`
  ${tw`font-semibold text-gray-800 text-lg`}
`;

const StepsContainer = styled.div`
  ${tw`mb-4`}
`;

const StepsList = styled.ol`
  ${tw`list-decimal list-inside space-y-2`}
`;

const StepItem = styled.li`
  ${tw`text-gray-700`}
`;

const ExpectedContainer = styled.div`
  ${tw`mb-4`}
`;

const ExpectedLabel = styled.div`
  ${tw`text-sm font-medium text-gray-500 mb-2`}
`;

const ExpectedBadges = styled.div`
  ${tw`flex flex-wrap gap-2`}
`;

const ActivityBadge = styled.span`
  ${tw`px-2 py-1 bg-green-100 text-green-700 rounded text-sm font-mono`}
`;

const VerificationContainer = styled.div`
  ${tw`bg-blue-50 rounded-lg p-3`}
`;

const VerificationLabel = styled.div`
  ${tw`text-sm font-medium text-blue-700 mb-1`}
`;

const VerificationText = styled.div`
  ${tw`text-blue-800 text-sm`}
`;

const QuickLinks = styled.div`
  ${tw`bg-gray-50 rounded-lg p-4 mb-6`}
`;

const QuickLinksTitle = styled.h3`
  ${tw`font-medium text-gray-700 mb-3`}
`;

const LinkGrid = styled.div`
  ${tw`grid grid-cols-2 md:grid-cols-4 gap-3`}
`;

const QuickLink = styled.a`
  ${tw`px-3 py-2 bg-white rounded border border-gray-200 text-sm text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-colors text-center`}
`;

const BookingTestFlowPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'types' | 'tests'>('types');

  return (
    <Container>
      <Header>
        <Title>Booking Activity Timeline - Test Flow</Title>
        <Subtitle>
          Comprehensive guide for testing the booking activity logging system across Web and Mobile
          platforms.
        </Subtitle>
      </Header>

      <QuickLinks>
        <QuickLinksTitle>Quick Links</QuickLinksTitle>
        <LinkGrid>
          <QuickLink href="/cars/host">Host Dashboard</QuickLink>
          <QuickLink href="/guest/mybookings">My Bookings</QuickLink>
          <QuickLink href="/invoices/refund-status">Refund Status</QuickLink>
          <QuickLink href="/cars/guest">Browse Cars</QuickLink>
        </LinkGrid>
      </QuickLinks>

      <TabContainer>
        <Tab active={activeTab === 'types'} onClick={() => setActiveTab('types')}>
          Activity Types ({ACTIVITY_TYPES.reduce((acc, cat) => acc + cat.types.length, 0)})
        </Tab>
        <Tab active={activeTab === 'tests'} onClick={() => setActiveTab('tests')}>
          Test Flows ({TEST_FLOWS.length})
        </Tab>
      </TabContainer>

      {activeTab === 'types' && (
        <Section>
          <SectionTitle>Supported Activity Types</SectionTitle>
          {ACTIVITY_TYPES.map(category => (
            <CategoryCard key={category.category}>
              <CategoryHeader>
                <CategoryTitle>{category.category}</CategoryTitle>
              </CategoryHeader>
              <ActivityTable>
                <tbody>
                  {category.types.map(activity => (
                    <TableRow key={activity.type}>
                      <IconCell>{activity.icon}</IconCell>
                      <TypeCell>{activity.type}</TypeCell>
                      <LabelCell>{activity.label}</LabelCell>
                      <DescCell>{activity.description}</DescCell>
                    </TableRow>
                  ))}
                </tbody>
              </ActivityTable>
            </CategoryCard>
          ))}
        </Section>
      )}

      {activeTab === 'tests' && (
        <Section>
          <SectionTitle>Manual Test Flows</SectionTitle>
          {TEST_FLOWS.map(flow => (
            <TestFlowCard key={flow.id}>
              <TestFlowHeader>
                <TestNumber>{flow.id}</TestNumber>
                <TestTitle>{flow.title}</TestTitle>
              </TestFlowHeader>

              <StepsContainer>
                <ExpectedLabel>Steps:</ExpectedLabel>
                <StepsList>
                  {flow.steps.map((step, index) => (
                    <StepItem key={index}>{step}</StepItem>
                  ))}
                </StepsList>
              </StepsContainer>

              <ExpectedContainer>
                <ExpectedLabel>Expected Activities:</ExpectedLabel>
                <ExpectedBadges>
                  {flow.expectedActivities.map((activity, index) => (
                    <ActivityBadge key={index}>{activity}</ActivityBadge>
                  ))}
                </ExpectedBadges>
              </ExpectedContainer>

              <VerificationContainer>
                <VerificationLabel>Verification:</VerificationLabel>
                <VerificationText>{flow.verification}</VerificationText>
              </VerificationContainer>
            </TestFlowCard>
          ))}
        </Section>
      )}
    </Container>
  );
};

export default BookingTestFlowPage;
